import React, { useCallback, useEffect } from 'react';
import { Container } from '@material-ui/core';
import { useParams, useHistory } from 'react-router-dom';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { observer } from 'mobx-react-lite';
import { HeaderBreadcrumbs, Page } from '../../../components';
import { ContactMessageForm } from '../Form';
import { ContactMessageValues, Link, QueryParams } from '../../../types';
import { useRequest, useStore } from '../../../hooks';
import { StateWrapper } from '../../../components/common/StateWrapper';
import rules from '../Form/rules';
import { PATH_DASHBOARD } from '../../../app';

const LINKS: Link[] = [
  { name: 'Dashboard', href: PATH_DASHBOARD.root },
  { name: 'Contact Messages', href: PATH_DASHBOARD.contactUs.list },
  { name: 'Edit' },
];

export const UpdateContactMessage: React.FC = observer(() => {
  const { id } = useParams<QueryParams>();
  const { push } = useHistory();

  const {
    contactUs: {
      getContactMessageById,
      updateContactMessage,
      fetchContactMessageById,
    },
  } = useStore();

  const message = getContactMessageById(id);

  const form = useForm<ContactMessageValues>({
    defaultValues: {
      name: '',
      email: '',
      country: '',
      city: '',
      message: '',
    },
    resolver: yupResolver(rules),
  });

  const handleUpdate = useCallback(async (values: ContactMessageValues) => {
    await updateContactMessage(id, values);
    push(PATH_DASHBOARD.contactUs.list);
  }, [id]);

  const { isFetching, isError } = useRequest({
    action: fetchContactMessageById,
    params: id,
    condition: !message,
  });

  useEffect(() => {
    if (message) {
      form.reset({
        name: message.name,
        email: message.email,
        country: message.country,
        city: message.city,
        message: message.message,
      });
    }
  }, [message]);

  return (
    <Page title="Edit Contact Message">
      <Container maxWidth="lg">
        <HeaderBreadcrumbs heading="Edit Contact Message" links={LINKS} />
        <FormProvider {...form}>
          <StateWrapper isError={isError} isReady={!!message && !isFetching}>
            <ContactMessageForm onAction={handleUpdate} />
          </StateWrapper>
        </FormProvider>
      </Container>
    </Page>
  );
});
